<script setup>
import { useStepper, useVModel } from '@vueuse/core';
import { ref, watch, onMounted } from 'vue';
import { kebabCase, omit } from 'lodash';
import { useUserPlatform } from '@/composable/platform';
import { useStaffs } from '@/queries/staff';
import Categories from '@/components/game/Categories.vue';
import Button from '@/components/ui/button/index.vue';
import Form from '@/components/form/Form.vue';
import Input from '@/components/form/Input.vue';
import Textarea from '@/components/form/Textarea.vue';

const props = defineProps({
    modelValue: {
        type: Object,
        default: null
    },
    initialValue: {
        type: Object,
        default: null
    },
    disableCategoryIds: {
        type: Array,
        default: () => []
    },
});

const { title } = useUserPlatform();
const { data: staffs } = useStaffs();
const emit = defineEmits(['update:modelValue', 'closed', 'done', 'reset']);
const modal = ref(null);
const category = useVModel(props, 'modelValue', emit);
const label = ref(null);
const description = ref(null);
const directors = ref([]);

const createDefaultDescription = () => {
    return `Welcome! This is ${title.value}'s discussion forum for ${category.value?.name}. Anyone with privileges can post comments and replies in the various Topics. Earn reputation points for being helpful, insightful, and funny!`;
}

// Watch for category changes to update label if label is empty
watch(category, (newCategory) => {
    if (newCategory) {
        label.value = newCategory.name;
        description.value = createDefaultDescription();
    }
}, { immediate: true });

const stepper = useStepper({
    'category': {
        isValid: () => !!category.value
    },
    'label': {
        isValid: () => !!label.value
    }
});

const handleClose = () => {
    handleReset();

    modal.value?.close();
    
    emit('closed');
};

const handleReset = () => {
    stepper.goTo('category');
    category.value = null;
    label.value = null;
    description.value = null;
}

const handleConfirm = () => {
    const cleanCategory = omit(category.value, ['parent', 'children']);
    emit('done', {
        category: cleanCategory,
        name: label.value,
        description: description.value || createDefaultDescription(),
        slug: kebabCase(label.value),
        icon: category.value.icon,
        staffs: directors.value,
    });

    handleClose();
}

const handleSkip = () => {
    const cleanCategory = omit(category.value, ['parent', 'children']);
    emit('done', {
        category: cleanCategory,
        name: category.value.name,
        description: category.value.name,
        slug: category.value.slug,
        icon: category.value.icon,
    });

    handleClose();
}
</script>

<template>
    <NModal ref="modal">
        <template #activator="{ on }">
            <slot name="activator" :on="on" :category="category" :opened="modal?.showModal" :handleReset="handleReset" />
        </template>

        <template #default="{ close }">
            <div  class="bg-white rounded-md border inline-block m-2 sm:w-auto sm:min-w-[870px]">
                <div v-if="stepper.isCurrent('category')">
                    <Categories v-model="category" :initialValue="initialValue" :disableCategoryIds="disableCategoryIds" />

                    <div class="flex justify-end gap-2 p-4 border-t">
                        <Button @click="handleClose" variant="text">
                            Cancel
                        </Button>
                        <Button @click="stepper.goToNext" variant="primary">
                            Confirm
                        </Button>
                    </div>
                </div>
                <Form v-if="stepper.isCurrent('label')" class="sm:min-h-[400px] w-[95dvw] sm:max-w-[870px] flex flex-col">
                    <div class="flex-1 flex flex-col p-5 sm:p-20 gap-4">
                        <div class="grid">
                            <label class="font-bold text-[1.05rem]">Category</label>
                            <button type="button" class="flex snap-end w-[10rem] min-h-[7.5rem] px-2 h-full text-center flex-col items-center justify-center py-4 rounded-md bg-dust-50 hover:bg-dust-100 border border-secondary-500 !text-dust-800" @click="stepper.goToPrevious">
                                <img :src="`/img/category-icons/${category.icon}`" :alt="category.name" class="h-10 filter invert-[70%] drop-shadow-md mb-3" @error="e => e.target.src = `/img/category-icons/article-draft-icon.svg`">
                                <span class="font-semibold">{{ category.name }}</span>
                            </button >
                        </div>
                        
                        <div class="grid">
                            <label class="font-bold text-[1.05rem]">Customize your channel name <span class="font-normal">(optional; 12 characters max)</span></label>
                            <Input name="label" v-model="label" :placeholder="category.name" :autofocus="true" maxlength="12" />
                        </div>

                        <div class="grid">
                            <label class="font-bold text-[1.05rem]">Channel welcome text <span class="font-normal">(edit if necessary)</span></label>
                            <Textarea name="description" v-model="description" />
                        </div>

                        <FeatureFlag v-if="staffs?.length" flag="manage-staffs">
                            <div class="grid">
                                <label class="font-bold text-[1.05rem]">Link this channel to directors</label>
                                <div class="flex items-center gap-2">
                                    <label v-for="staff in staffs" :key="staff.id" :for="staff.platform_id" class="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            :id="staff.platform_id"
                                            :name="staff.platform_id"
                                            :value="staff.platform_id"
                                            v-model="directors"
                                            class="h-5 w-5 rounded"
                                        />
                                        <span>{{ staff.platform.title }}</span>
                                    </label>
                                </div>
                            </div>
                        </FeatureFlag>
                    </div>  
                    <div class="flex justify-end gap-2 p-4 border-t">
                        <Button @click="stepper.goToPrevious" variant="ghost">
                            Back
                        </Button>
                        <Button @click="handleConfirm" variant="primary" :disabled="!stepper.current.value.isValid()">
                            Save
                        </Button>
                    </div>
                </Form>
                
            </div>
        </template>
    </NModal>
</template>